/**
 * placeholderNode.ts — single source of truth for "is this a placeholder?".
 *
 * A placeholder node is a synthesized canvas node that has no real on-chain
 * address. They appear via several paths:
 *
 *   - `ghost:{link_id}:source` — counterparty withheld in a subpoena
 *     (forensic overlay; data.forensic === 'ghost', data.forensic_ghost = true)
 *   - `__coinbase__`, `__op_return__`, `__contract_creation__` — BTC/EVM
 *     stand-ins for non-address counterparties (data.type === 'placeholder')
 *
 * Why centralise: every code path that does something with the node's id
 * (fetch transactions, lookup entity, run dossier, kick off enrichment)
 * must short-circuit, otherwise it sends the synthetic id to a backend
 * that returns garbage AND back-propagates that garbage onto the canvas
 * — which strips the very fields that mark the node as a placeholder.
 *
 * Each site re-deriving "is placeholder" from scratch is the bug we keep
 * regressing. Use these helpers everywhere.
 */

/** Prefixes for synthesized ids. Add new ones here, never inline.
 *  - 'ghost:'    — undisclosed-counterparty subpoena placeholders
 *  - '__'        — chain-namespaced stand-ins (__coinbase__, __op_return__, __contract_creation__)
 *  - 'cluster:'  — starburst-bundle nodes (bundleStarburstEdges output) — also have no real address
 *  - 'attr-hub:' — attribution-hub nodes (Workbench.tsx:5448) that bundle every
 *                  address sharing one entity label (e.g. all "Binance. User"
 *                  addresses) into one virtual canvas node. Sending its id to
 *                  /addresses/{chain}/{address}/* produces 422s because the id
 *                  isn't a real on-chain address.
 *  - 'social:'   — Instagram OSINT social_handle hub nodes (social:instagram:<handle>).
 *                  A Tier-4 profile node, not an on-chain address — never trace/fetch it.
 *  - 'synthetic-in-' / 'synthetic-out-' — "[ + N More ]" leaf-pruning group
 *                  from graphLimiters.pruneExcessiveLeaves. Hyphenated ids, so
 *                  the underscore heuristic does not catch them.
 */
export const PLACEHOLDER_ID_PREFIXES = [
    'ghost:', '__', 'cluster:', 'attr-hub:', 'social:',
    'synthetic-in-', 'synthetic-out-',
] as const;

/**
 * True iff the id string itself is synthesized (no real address).
 * Cheaper than the full check — use when you only have an id, not a node.
 *
 * Also returns true for demo-seed labels like `0xRUGPULL_DEPLOYER_111…`.
 * Real on-chain addresses never contain underscores — base58, hex, bech32,
 * and cashaddr all exclude `_` — so the underscore is a reliable signal
 * the string is a human-readable placeholder, not an address.
 */
export function isPlaceholderId(id: unknown): boolean {
    if (typeof id !== 'string' || id.length === 0) return false;
    for (const p of PLACEHOLDER_ID_PREFIXES) {
        if (id.startsWith(p)) return true;
    }
    if (id.includes('_')) return true;
    if (/^(coinbase|null|none)$/i.test(id.trim())) return true;
    return false;
}

/**
 * True iff the canvas node represents a placeholder. Inspects both the id
 * and the data flags so it works regardless of which producer minted the
 * node (forensic overlay, on-chain stand-in, future synthesizers).
 *
 * Accepts: GraphNodeData (flat), Cytoscape `{data}` envelope, or a raw object.
 */
export function isPlaceholderNode(node: any): boolean {
    if (!node) return false;
    const data = node.data && typeof node.data === 'object' ? node.data : node;
    if (isPlaceholderId(data.id)) return true;
    if (data.type === 'placeholder') return true;
    if (data.type === 'synthetic-group') return true;
    if (data.is_synthetic === true) return true;
    if (data.is_placeholder === true) return true;
    if (data.forensic_ghost === true) return true;
    if (data.forensic === 'ghost') return true;
    return false;
}

/**
 * Defensive merge: when enrichment writes new fields onto an existing node,
 * preserve the placeholder marker fields so downstream consumers keep
 * recognising it as a placeholder. Use this anywhere you compute a merged
 * data dict — better than relying on every call-site to remember which
 * fields are load-bearing.
 *
 * Example:
 *     const merged = preservePlaceholderFields(existing.data, {
 *         ...existing.data,
 *         entity_name: 'Binance',
 *         label: 'Binance hot wallet',
 *     });
 */
export function preservePlaceholderFields<T extends Record<string, any>>(
    original: any,
    next: T,
): T {
    if (!original || !isPlaceholderNode(original)) return next;
    const out: any = { ...next };
    // Only restore what the original had — never invent flags.
    if (original.type === 'placeholder' && next.type !== 'placeholder') {
        out.type = 'placeholder';
    }
    if (original.is_placeholder === true && next.is_placeholder !== true) {
        out.is_placeholder = true;
    }
    if (original.forensic_ghost === true && next.forensic_ghost !== true) {
        out.forensic_ghost = true;
    }
    if (original.forensic && !next.forensic) {
        out.forensic = original.forensic;
    }
    return out as T;
}
